<script setup>
import { computed, reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'next/input/Input.vue';

const props = defineProps({
  value: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const state = reactive({
  name: props.value.name || '',
  url: props.value.url || '',
});

// Custom validator: only allow whatsgpt.net host (including subdomains like app.whatsgpt.net)
const hostIsWhatsgptNet = value => {
  if (!value) return false;
  try {
    const hostname = new URL(value).hostname.toLowerCase();
    return hostname === 'whatsgpt.net' || hostname.endsWith('.whatsgpt.net');
  } catch {
    return false;
  }
};

const rules = {
  name: {
    required,
  },
  url: {
    required,
    url,
    hostIsWhatsgptNet,
  },
};

const v$ = useVuelidate(rules, state);

const nameError = computed(() => {
  if (!v$.value.name.$error) return '';
  if (v$.value.name.required.$invalid)
    return t('WGPT_CUSTOM_APPS.FORM.NAME.ERRORS.REQUIRED');

  return '';
});
const urlError = computed(() => {
  if (!v$.value.url.$error) return '';
  if (v$.value.url.required.$invalid)
    return t('WGPT_CUSTOM_APPS.FORM.URL.ERRORS.REQUIRED');
  if (v$.value.url.url.$invalid)
    return t('WGPT_CUSTOM_APPS.FORM.URL.ERRORS.INVALID');
  if (v$.value.url.hostIsWhatsgptNet.$invalid)
    return t('WGPT_CUSTOM_APPS.FORM.URL.ERRORS.HOST_INVALID');

  return '';
});

const onSubmit = () => {
  emit('submit');
};

defineExpose({ state, v$ });
</script>

<template>
  <form class="flex flex-col gap-6 w-full" @submit.prevent="onSubmit">
    <Input
      v-model="state.name"
      :label="t('WGPT_CUSTOM_APPS.FORM.NAME.LABEL')"
      :message="nameError"
      :message-type="nameError ? 'error' : 'info'"
      @input="v$.name.$touch"
    />

    <Input
      v-model="state.url"
      :label="t('WGPT_CUSTOM_APPS.FORM.URL.LABEL')"
      :message="urlError"
      :message-type="urlError ? 'error' : 'info'"
      type="url"
      @input="v$.url.$touch"
    />
  </form>
</template>
